import { useContext, useEffect } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { ReactComponent as ArrowRightIcon } from 'assets/arrow-right.svg';
import EmptyTableContentPlug from 'components/EmptyTableContentPlug';
import Loader from 'components/Loader';
import i18nContext from 'components/i18n-context';
import { ROUTE_PATHS } from 'routes/constants';
import IntegrationProvider from 'pages/Settings/components/IntegrationProvider';
import IntegrationSettingSourceBadge from 'pages/Settings/components/IntegrationSettingSourceBadge';
import { getSettingChangeText, getSettingLabel } from 'pages/Settings/integrationSettingLabels';
import './IntegrationSettingsTab.scss';

const groupByProvider = (settings) =>
  settings.reduce((groups, setting) => {
    const group = groups.find(({ provider }) => provider === setting.provider);
    if (group) {
      group.settings.push(setting);
    } else {
      groups.push({ provider: setting.provider, settings: [setting] });
    }
    return groups;
  }, []);

const IntegrationSettingsTab = ({ integrationSettingsStore }) => {
  const i18n = useContext(i18nContext);
  const navigate = useNavigate();

  useEffect(() => {
    integrationSettingsStore.getIntegrationSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!integrationSettingsStore.isInitialized) {
    return <Loader className={'application-loader'} />;
  }

  const openSetting = ({ provider, settingKey }) =>
    navigate(generatePath(ROUTE_PATHS.SETTINGS_INTEGRATION_SETTING, { provider, settingKey }));

  return (
    <div className={'integration-settings-wrapper'}>
      <div className={'integration-settings-header'}>
        <h3 className={'integration-settings-title'}>{i18n.getMessage('settings.integrations.title')}</h3>
        <p className={'integration-settings-description'}>{i18n.getMessage('settings.integrations.description')}</p>
      </div>
      {integrationSettingsStore.settings.length === 0 ? (
        <EmptyTableContentPlug
          headerText={i18n.getMessage('settings.integrations.empty.header')}
          descriptionText={i18n.getMessage('settings.integrations.empty.description')}
        />
      ) : (
        <div className={'integration-settings-providers'}>
          {groupByProvider(integrationSettingsStore.settings).map(({ provider, settings }) => (
            <section key={provider} className={'integration-settings-provider'}>
              <header className={'integration-settings-provider-header'}>
                <IntegrationProvider provider={provider} />
              </header>
              <ul className={'integration-settings-items'}>
                {settings.map((setting) => (
                  <li key={setting.settingKey}>
                    <button
                      type={'button'}
                      className={'integration-settings-item'}
                      onClick={() => openSetting(setting)}
                    >
                      <span className={'integration-settings-item-main'}>
                        <span className={'integration-settings-item-title'}>
                          {getSettingLabel(setting.settingKey)}
                          <code className={'integration-code'}>{setting.settingKey}</code>
                        </span>
                        {setting.description && (
                          <span className={'integration-settings-item-description'} title={setting.description}>
                            {setting.description}
                          </span>
                        )}
                      </span>
                      <span className={'integration-settings-item-state'}>
                        <IntegrationSettingSourceBadge source={setting.source} />
                        <span className={'integration-settings-item-meta'}>
                          {getSettingChangeText(i18n, setting, integrationSettingsStore.getOperatorName)}
                        </span>
                      </span>
                      <span className={'integration-settings-item-version'}>
                        {i18n.getMessage('settings.integrations.meta.versionShort', { version: setting.version })}
                      </span>
                      <ArrowRightIcon className={'integration-settings-item-arrow'} />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

IntegrationSettingsTab.propTypes = {
  integrationSettingsStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  integrationSettingsStore: stores.integrationSettingsStore
}))(observer(IntegrationSettingsTab));
